import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { dossierAssetUrl } from './dossierAssetUrl';

/**
 * Profile photos are fetched like every other call to the API (the same client, the same cookie, the same CORS rules) and shown
 * from the bytes, instead of giving the browser the photo's address to load on its own.
 *
 * Why: in production the page and the API are different sites. A plain <img> of an API address is a no-cors request: the API's
 * `Cross-Origin-Resource-Policy: same-origin` (helmet's default, a good one for HR files) makes the browser refuse to show it, so
 * every photo came up broken on live while they worked in dev, where a proxy makes the page and the API one origin. A request through
 * the API client is a normal CORS request, which the API already allows for the page, and it needs no weaker header on the files.
 */

// address -> object URL of the downloaded bytes, kept for the life of the page (a photo's address changes whenever the photo does)
const resolved = new Map();
// address -> the download in progress, so a chart of 85 avatars asks for each photo once
const inFlight = new Map();

export const loadProtectedImage = (url) => {
  const address = dossierAssetUrl(url);
  if (!address) return Promise.resolve('');
  if (resolved.has(address)) return Promise.resolve(resolved.get(address));
  if (!inFlight.has(address)) {
    // Inside a promise, so a failing request (or a test double that returns nothing) is a failed photo and never a thrown error.
    inFlight.set(address, Promise.resolve().then(() => api.get(address, { responseType: 'blob' }))
      .then((res) => {
        const objectUrl = URL.createObjectURL(res.data);
        resolved.set(address, objectUrl);
        return objectUrl;
      })
      .finally(() => inFlight.delete(address)));
  }
  return inFlight.get(address);
};

/** The photo's bytes, for the Set the photo window (which draws it on a canvas, so it cannot be a cross-site <img>). */
export const fetchProtectedBlob = async (url) => (await api.get(dossierAssetUrl(url), { responseType: 'blob' })).data;

/** { src, failed }: `src` is empty until the photo has arrived. */
export function useProtectedImage(url) {
  const address = dossierAssetUrl(url);
  const [state, setState] = useState(() => ({ address, src: resolved.get(address) || '', failed: false }));

  useEffect(() => {
    if (!address) {
      setState({ address, src: '', failed: false });
      return undefined;
    }
    if (resolved.has(address)) {
      setState({ address, src: resolved.get(address), failed: false });
      return undefined;
    }
    let cancelled = false;
    setState({ address, src: '', failed: false });
    loadProtectedImage(url)
      .then((src) => { if (!cancelled) setState({ address, src, failed: !src }); })
      .catch(() => { if (!cancelled) setState({ address, src: '', failed: true }); });
    return () => { cancelled = true; };
  }, [address, url]);

  // A new address never shows the previous photo while its own is on the way.
  return state.address === address ? { src: state.src, failed: state.failed } : { src: '', failed: false };
}

/** An <img> of a stored photo. `fallback` shows while it loads (and if it fails, unless `failedFallback` says otherwise). */
export default function ProtectedImage({ url, alt = '', fallback = null, failedFallback, ...imgProps }) {
  const { src, failed } = useProtectedImage(url);
  if (src) return <img src={src} alt={alt} {...imgProps} />;
  if (failed && failedFallback !== undefined) return failedFallback;
  return fallback;
}
